import {createUniversalFetch, navigateTo, useAuth, useLocalePrefix, useLogger, useMaltEnvironnement, useRuntimeConfig, type Language} from '#imports';

const universalFetch = createUniversalFetch({baseURL: '/navbar'});

export function useLocalizationSelection() {
    const logger = useLogger();
    const {loggedIn} = useAuth();
    const runtimeConfig = useRuntimeConfig();
    const {withLocalePrefix, withoutLocalePrefix} = useLocalePrefix();

    async function changeLanguageAndRedirect(selectedLanguage: Language) {
        if (selectedLanguage.host) {
            if (loggedIn) {
                await _changeLanguageAndRedirectForLoggedInUser(selectedLanguage.host);
            } else {
                await _changeLanguageAndRedirectForUnloggedUser(selectedLanguage.host, selectedLanguage.locale);
            }
        }
    }

    // @XAuthTours(step = tour_send_redirect, includeCode = true)
    async function _changeLanguageAndRedirectForLoggedInUser(newHost: string): Promise<void> {
        // lib-front-shared-domains/nuxt-navbar-module/src/runtime/logged/features/language-selector/language-selector.store.ts
        try {
            const redirectUrl = new URL(window.location.toString());
            redirectUrl.searchParams.delete('lg');
            // Remove any existing locale prefix when changing domain
            redirectUrl.pathname = withoutLocalePrefix(redirectUrl.pathname) as string;
            redirectUrl.host = newHost;

            const userToken = await universalFetch<string>('/api/user/x-auth-token');
            await universalFetch('/api/user/unsudosu', {method: 'POST'});

            const xAuthUrl = new URL(`${window.location.protocol}//${newHost}/navbar/api/xauth`);
            xAuthUrl.searchParams.append('redirectUrl', redirectUrl.toString());
            xAuthUrl.searchParams.append('x-auth', userToken);

            navigateTo(xAuthUrl.toString(), {external: true});
        } catch (e) {
            logger.error("Can't change language", e);
        }
    }
    function _changeLanguageAndRedirectForUnloggedUser(newHost: string, locale?: string) {
        if (newHost) {
            const url = new URL(window.location.href);

            const {enableLocalePrefixStrategy, supportedLocalePrefixes} = runtimeConfig.public['@malt/nuxt-i18n-module'];
            const maybeLocalePrefix = locale?.replace('_', '-').toLowerCase();
            const hasSupportedLocalePrefix = (supportedLocalePrefixes as {prefix: string}[] | undefined)?.some(
                ({prefix}) => prefix === maybeLocalePrefix,
            );
            const shouldUseLocalePrefixStrategy = locale && enableLocalePrefixStrategy && hasSupportedLocalePrefix;

            if (shouldUseLocalePrefixStrategy) {
                const localePrefix = locale?.replace('_', '-').toLowerCase(); // fr_FR -> fr-fr
                url.host = getMultiLocaleHost();
                url.pathname = withLocalePrefix(url.pathname, localePrefix) as string;
            } else {
                // Remove any existing locale prefix when redirecting to a classic domain
                url.pathname = withoutLocalePrefix(url.pathname) as string;
                url.host = newHost;
            }

            navigateTo(url.toString(), {external: true});
        }
    }

    function getMultiLocaleHost() {
        const environment = useMaltEnvironnement();

        switch (environment) {
            case 'DEV':
                return 'dev.malt.com';
            case 'DEV_INTEGRATION':
                return 'dev.integration.malt.com';
            case 'INTEGRATION':
                return 'www.integration.malt.com';
            case 'PRODUCTION':
            case 'UNKNOWN':
            default:
                return 'www.malt.com';
        }
    }

    return {
        // functions A-Z
        changeLanguageAndRedirect,
        // refs A-Z
    };
}
